import React, {
  Fragment,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FuelPlanStop } from "types";
import {
  getCalculatorFuelingLabel,
  getCalculatorLeg,
  SelectedFuelStops,
} from "pages/fuel-plan/fuel-plan-display";
import {
  CalculatorInputField,
  CalculatorInputState,
  CalculatorState,
  MINIMUM_PURCHASE_GALLONS,
  POUNDS_PER_GALLON,
  SAFETY_BUFFER_GALLONS,
} from "pages/fuel-plan/fuel-plan-calculator";

type TargetGallonsCalculatorProps = {
  calculatorRef: RefObject<HTMLElement>;
  calculator: CalculatorState;
  calculatorInputs: CalculatorInputState;
  maxFuelRangeMiles: number | null;
  selectedCalculatorStop: FuelPlanStop | null;
  selectedFuelStops: SelectedFuelStops;
  timelineStops: FuelPlanStop[];
  onAvailableRangeChange: (value: string) => void;
  onCalculatorInputChange: (field: CalculatorInputField, value: string) => void;
  onCalculate: () => void;
};

type CalculatorFieldProps = {
  disabled: boolean;
  label: string;
  suffix: string;
  value: string;
  onChange: (value: string) => void;
};

const CalculatorField = ({
  disabled,
  label,
  suffix,
  value,
  onChange,
}: CalculatorFieldProps) => (
  <label className="fuel-plan-field">
    {label}
    <span>
      <input
        disabled={disabled}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <small>{suffix}</small>
    </span>
  </label>
);

const TargetGallonsCalculator = ({
  calculatorRef,
  calculator,
  calculatorInputs,
  maxFuelRangeMiles,
  selectedCalculatorStop,
  selectedFuelStops,
  timelineStops,
  onAvailableRangeChange,
  onCalculatorInputChange,
  onCalculate,
}: TargetGallonsCalculatorProps) => {
  const [
    isMinimumPurchaseInfoVisible,
    setIsMinimumPurchaseInfoVisible,
  ] = useState(false);
  const minimumPurchaseInfoRef = useRef<HTMLDivElement | null>(null);
  const hasSelectedLeg = selectedCalculatorStop !== null;
  const selectedLeg = useMemo(
    () =>
      selectedCalculatorStop
        ? getCalculatorLeg(
            selectedCalculatorStop,
            timelineStops,
            selectedFuelStops,
          )
        : null,
    [selectedCalculatorStop, selectedFuelStops, timelineStops],
  );
  const fuelingAtLabel = useMemo(
    () => getCalculatorFuelingLabel(selectedCalculatorStop, selectedFuelStops),
    [selectedCalculatorStop, selectedFuelStops],
  );
  const targetFuelWeightPounds = Math.round(
    calculator.targetGallons * POUNDS_PER_GALLON,
  );

  useEffect(() => {
    if (!isMinimumPurchaseInfoVisible) return;

    const closeMinimumPurchaseInfo = (event: MouseEvent | TouchEvent) => {
      if (minimumPurchaseInfoRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsMinimumPurchaseInfoVisible(false);
    };

    document.addEventListener("mousedown", closeMinimumPurchaseInfo);
    document.addEventListener("touchstart", closeMinimumPurchaseInfo);

    return () => {
      document.removeEventListener("mousedown", closeMinimumPurchaseInfo);
      document.removeEventListener("touchstart", closeMinimumPurchaseInfo);
    };
  }, [isMinimumPurchaseInfoVisible]);

  return (
    <aside className="fuel-plan-calculator" ref={calculatorRef}>
      <h2>Target Gallons Calculator</h2>

      <div className="fuel-plan-selected-leg">
        <span>
          <small>Leg:</small>
          <strong>
            {selectedLeg ? (
              <>
                {selectedLeg.fromDisplayName} &rarr; {selectedLeg.toDisplayName}
              </>
            ) : (
              "None selected"
            )}
          </strong>
        </span>
        <span>
          <small>Fueling at:</small>
          <strong>{fuelingAtLabel}</strong>
        </span>
        <span className="fuel-plan-max-range">
          <small>Max range for this truck:</small>
          <strong>
            {maxFuelRangeMiles === null ? "N/A" : `${maxFuelRangeMiles} mi`}
          </strong>
        </span>
      </div>

      <CalculatorField
        disabled={!hasSelectedLeg}
        label="Available truck fuel range"
        onChange={onAvailableRangeChange}
        suffix="mi"
        value={calculatorInputs.currentFuelRangeMiles}
      />
      <CalculatorField
        disabled={!hasSelectedLeg}
        label="Mileage to next fueling location"
        onChange={(value) => onCalculatorInputChange("milesToNextStop", value)}
        suffix="mi"
        value={calculatorInputs.milesToNextStop}
      />
      <CalculatorField
        disabled={!hasSelectedLeg}
        label="Average MPG"
        onChange={(value) => onCalculatorInputChange("averageMpg", value)}
        suffix="mpg"
        value={calculatorInputs.averageMpg}
      />

      <button
        className="fuel-plan-calculate"
        disabled={!hasSelectedLeg}
        onClick={onCalculate}
        type="button"
      >
        Calculate
      </button>

      <div className="fuel-plan-reserve">
        <span>Reserve</span>
        <strong>{hasSelectedLeg ? `${SAFETY_BUFFER_GALLONS} gal` : ""}</strong>
        {hasSelectedLeg ? <small>Locked</small> : null}
      </div>

      <div className="fuel-plan-result" ref={minimumPurchaseInfoRef}>
        <div className="fuel-plan-result-heading">
          <small>Target purchase</small>
          {hasSelectedLeg ? (
            <button
              aria-expanded={isMinimumPurchaseInfoVisible}
              aria-label="Explain minimum fuel purchase"
              onClick={(event) => {
                event.stopPropagation();
                setIsMinimumPurchaseInfoVisible((isVisible) => !isVisible);
              }}
              type="button"
            >
              i
            </button>
          ) : null}
        </div>
        {hasSelectedLeg ? (
          <Fragment>
            <strong>
              {calculator.targetGallons} <span>gal</span>
            </strong>
            <p>~ {targetFuelWeightPounds} lbs of fuel</p>
            <em>{MINIMUM_PURCHASE_GALLONS} gal minimum</em>
          </Fragment>
        ) : (
          <div className="fuel-plan-result-empty" />
        )}
        {hasSelectedLeg && isMinimumPurchaseInfoVisible ? (
          <div className="fuel-plan-minimum-info">
            The calculator shows the larger of the fuel needed for this leg or
            the {MINIMUM_PURCHASE_GALLONS} gallon minimum purchase. If the math
            says you need less than {MINIMUM_PURCHASE_GALLONS} gallons, the
            target still shows {MINIMUM_PURCHASE_GALLONS} gallons because
            smaller fuel purchases are not recommended for this plan.
          </div>
        ) : null}
      </div>
    </aside>
  );
};

export default TargetGallonsCalculator;
